import dayjs from 'dayjs';
import {
  buildOpenClinicalTasksSearch,
  buildRecentlyCompletedClinicalTasksSearch,
  TASK_LOG_MAX_ROWS,
  TASK_LOG_PAGE_SIZE,
  compareClinicalTasks,
  describeClinicalTaskDue,
  getClinicalTaskCompletedBy,
  getClinicalTaskDueDate,
} from '@bariendo/lib/clinical-tasks';
import { Container, Group, Paper, Stack, Tabs, Text, Title } from '@mantine/core';
import { formatDateTime, resolveId } from '@medplum/core';
import { Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getClinicalTaskAssignmentLabel, getClinicalTaskTeamLabel, TaskTeamLabel } from '@/services/task-management';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import { TaskGroupIdentifier, TIMELINE_INFO_TAB_QUERY_PARAM } from '@bariendo/lib/constants';
import { useSubscriptionWithLimitHandling } from '@/services/subscriptions';

const COMPLETED_WINDOW_DAYS = 14;

/** One top tab per team; short tab labels, bucketing keys from TASK_TEAMS. */
const TASK_LOG_TEAM_TABS: { value: TaskTeamLabel; tabLabel: string }[] = [
  { value: 'Intake', tabLabel: 'Intake' },
  { value: 'Care operations', tabLabel: 'Care Ops' },
  { value: 'Care team', tabLabel: 'Care Team' },
  { value: 'Other', tabLabel: 'Other' },
];

type TaskLogTab = TaskTeamLabel | 'completed';

/**
 * Cross-patient monitoring view of clinical tasks, under Communication logs.
 * Both tabs are server-side filtered searches (group-identifier + status
 * [+ modified date]) — indexed token queries, never a full Task scan.
 */
export function TaskLogPage(): ReactElement {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TaskLogTab>('Intake');
  const [outstanding, setOutstanding] = useState<Task[]>([]);
  const [completed, setCompleted] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  /** Server-reported total open tasks; > outstanding.length means we truncated. */
  const [outstandingTotal, setOutstandingTotal] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const since = dayjs().subtract(COMPLETED_WINDOW_DAYS, 'day').toISOString();

      // Page through the WHOLE open set (a bounded working set — tasks get
      // completed), so the per-team tab counts are true rather than derived
      // from an arbitrary first page. TASK_LOG_MAX_ROWS is the runaway guard,
      // surfaced in the UI when hit. cache: 'reload' because this monitoring
      // surface refreshes by subscription and the 60s GET cache is only
      // invalidated by local writes.
      const collectOpenTasks = async (): Promise<{ tasks: Task[]; total: number }> => {
        const collected: Task[] = [];
        let total = 0;
        for (let offset = 0; offset < TASK_LOG_MAX_ROWS; offset += TASK_LOG_PAGE_SIZE) {
          const bundle = await medplum.search('Task', buildOpenClinicalTasksSearch(offset), { cache: 'reload' });
          total = bundle.total ?? collected.length;
          const page = (bundle.entry ?? []).flatMap((entry) => (entry.resource ? [entry.resource as Task] : []));
          collected.push(...page);
          if (page.length < TASK_LOG_PAGE_SIZE || collected.length >= total) {
            break;
          }
        }
        return { tasks: collected, total };
      };

      const [open, completedResults] = await Promise.all([
        collectOpenTasks(),
        medplum.searchResources('Task', buildRecentlyCompletedClinicalTasksSearch(since), { cache: 'reload' }),
      ]);
      const now = new Date();
      setOutstandingTotal(open.total);
      setOutstanding([...open.tasks].sort((a, b) => compareClinicalTasks(a, b, now)));
      setCompleted(
        [...completedResults].sort(
          (a, b) => new Date(b.lastModified ?? 0).getTime() - new Date(a.lastModified ?? 0).getTime()
        )
      );
    } catch {
      // Keep last-known-good; the page has no toast surface worth spamming.
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Keep the monitoring view live. Admin-only surface, so this project-wide
  // criteria has a bounded audience (a handful of admins), unlike the
  // per-user badges.
  useSubscriptionWithLimitHandling(
    `Task?group-identifier=${TaskGroupIdentifier.ORBIT_PROVIDER_TASK}`,
    () => void refresh()
  );

  const taskColumn: MRT_ColumnDef<Task> = useMemo(
    () => ({
      id: 'task',
      header: 'Task',
      size: 280,
      enableSorting: false,
      accessorFn: (task) => task.description,
      Cell: ({ cell }) => (
        <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>
          {cell.getValue<string>()}
        </Text>
      ),
    }),
    []
  );

  const patientColumn: MRT_ColumnDef<Task> = useMemo(
    () => ({
      id: 'patient',
      header: 'Patient',
      size: 180,
      enableSorting: false,
      accessorFn: (task) => task.for?.display ?? '',
      Cell: ({ cell }) => <Text size="sm">{cell.getValue<string>()}</Text>,
    }),
    []
  );

  const outstandingColumns = useMemo<MRT_ColumnDef<Task>[]>(
    () => [
      taskColumn,
      patientColumn,
      {
        id: 'assignedTo',
        header: 'Assigned to',
        size: 160,
        enableSorting: false,
        accessorFn: (task) => getClinicalTaskAssignmentLabel(task),
        Cell: ({ cell }) => <Text size="sm">{cell.getValue<string>()}</Text>,
      },
      {
        id: 'due',
        header: 'Due',
        size: 150,
        enableSorting: false,
        accessorFn: (task) => getClinicalTaskDueDate(task),
        Cell: ({ row }) => {
          const due = getClinicalTaskDueDate(row.original);
          if (!due) {
            return (
              <Text size="sm" c="var(--mantine-color-gray-5)">
                No due date
              </Text>
            );
          }
          const description = describeClinicalTaskDue(due);
          return (
            <Text
              component="time"
              size="sm"
              style={{ whiteSpace: 'nowrap' }}
              title={dayjs(due).format('MMMM D, YYYY')}
              c={description.overdue ? 'var(--mantine-color-error)' : undefined}
            >
              {description.label}
            </Text>
          );
        },
      },
      {
        id: 'created',
        header: 'Created',
        size: 150,
        enableSorting: false,
        accessorFn: (task) => task.authoredOn,
        Cell: ({ cell }) => <Text size="sm">{formatDateTime(cell.getValue<string>())}</Text>,
      },
    ],
    [taskColumn, patientColumn]
  );

  const completedColumns = useMemo<MRT_ColumnDef<Task>[]>(
    () => [
      taskColumn,
      patientColumn,
      {
        id: 'completedBy',
        header: 'Completed by',
        size: 160,
        enableSorting: false,
        accessorFn: (task) => getClinicalTaskCompletedBy(task)?.display ?? '',
        Cell: ({ cell }) => {
          const name = cell.getValue<string>();
          return name ? (
            <Text size="sm">{name}</Text>
          ) : (
            <Text size="sm" c="var(--mantine-color-gray-5)">
              —
            </Text>
          );
        },
      },
      {
        id: 'completedAt',
        header: 'Completed',
        size: 150,
        enableSorting: false,
        accessorFn: (task) => task.lastModified,
        Cell: ({ cell }) => <Text size="sm">{dayjs(cell.getValue<string>()).fromNow()}</Text>,
      },
    ],
    [taskColumn, patientColumn]
  );

  const rowNavigationProps = useCallback(
    ({ row }: { row: { original: Task } }) => ({
      onClick: () => {
        const patientId = row.original.for ? resolveId(row.original.for) : undefined;
        if (patientId) {
          // Same landing as the inbox: straight onto the patient's Tasks tab.
          navigate(`/Patient/${patientId}/timeline?${TIMELINE_INFO_TAB_QUERY_PARAM}=tasks`);
        }
      },
      style: { cursor: 'pointer' },
    }),
    [navigate]
  );

  const commonTableOptions = {
    rowCount: 0,
    initialState: { density: 'xs' as const },
    enableRowSelection: false,
    enableSorting: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableColumnActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowVirtualization: true,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    mantineTableProps: { highlightOnHover: true },
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' as const },
    },
    mantineTableHeadCellProps: { style: { fontWeight: '500' } },
  };

  // Assignee's practitioner id → primary role, for bucketing individually
  // assigned tasks into their person's team. Same session roster the avatars use.
  const { roleByPractitionerId } = useProviderDirectory().directory;

  const outstandingByTeam = useMemo(() => {
    const buckets = new Map<TaskTeamLabel, Task[]>();
    for (const task of outstanding) {
      const team = getClinicalTaskTeamLabel(task, roleByPractitionerId);
      const bucket = buckets.get(team);
      if (bucket) {
        bucket.push(task);
      } else {
        buckets.set(team, [task]);
      }
    }
    return buckets;
  }, [outstanding, roleByPractitionerId]);

  const activeTeamTasks = activeTab === 'completed' ? [] : (outstandingByTeam.get(activeTab) ?? []);

  const outstandingTable = useMantineReactTable({
    ...commonTableOptions,
    columns: outstandingColumns,
    data: activeTeamTasks,
    rowCount: activeTeamTasks.length,
    state: { isLoading: loading },
    mantineTableBodyRowProps: rowNavigationProps,
  });

  const completedTable = useMantineReactTable({
    ...commonTableOptions,
    columns: completedColumns,
    data: completed,
    rowCount: completed.length,
    state: { isLoading: loading },
    mantineTableBodyRowProps: rowNavigationProps,
  });

  return (
    <Container size="xl" py="md" style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group justify="space-between" align="center">
          <Title order={1}>Task log</Title>
        </Group>

        {outstandingTotal > outstanding.length && (
          <Text size="sm" c="var(--mantine-color-error)">
            Showing {outstanding.length} of {outstandingTotal} outstanding tasks — team counts below are incomplete.
          </Text>
        )}

        <Tabs value={activeTab} onChange={(value) => setActiveTab((value as TaskLogTab) ?? 'Intake')}>
          <Tabs.List>
            {TASK_LOG_TEAM_TABS.map((team) => (
              <Tabs.Tab key={team.value} value={team.value}>
                Outstanding - {team.tabLabel} ({(outstandingByTeam.get(team.value) ?? []).length})
              </Tabs.Tab>
            ))}
            <Tabs.Tab value="completed">
              Completed - last {COMPLETED_WINDOW_DAYS} days ({completed.length})
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>

        <Paper style={{ flex: 1, overflow: 'hidden' }}>
          {activeTab === 'completed' ? (
            <MantineReactTable table={completedTable} />
          ) : (
            <MantineReactTable table={outstandingTable} />
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
